import type { FC, ReactNode } from 'react';
import { motion } from 'framer-motion';

export interface FadeInProps {
  children: ReactNode;
  /**
   * Animation duration in seconds
   * @default 0.4
   */
  duration?: number;
  /**
   * Delay before animation starts in seconds
   * @default 0
   */
  delay?: number;
  /**
   * Direction to slide from
   * @default 'up'
   */
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  /**
   * Distance to slide in pixels
   * @default 20
   */
  distance?: number;
  /**
   * Custom class name
   */
  className?: string;
}

/**
 * FadeIn animation wrapper component
 * Provides a smooth fade-in animation with optional directional slide
 * Uses framer-motion for performant animations
 *
 * @example
 * ```tsx
 * <FadeIn delay={0.2} direction="up">
 *   <YourContent />
 * </FadeIn>
 * ```
 */
export const FadeIn: FC<FadeInProps> = ({
  children,
  duration = 0.4,
  delay = 0,
  direction = 'up',
  distance = 20,
  className,
}) => {
  const getInitialPosition = () => {
    switch (direction) {
      case 'up':
        return { y: distance };
      case 'down':
        return { y: -distance };
      case 'left':
        return { x: distance };
      case 'right':
        return { x: -distance };
      case 'none':
      default:
        return {};
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, ...getInitialPosition() }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{
        duration,
        delay,
        ease: 'easeOut',
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};
